<script lang="ts" setup>
import { computed, ref } from 'vue';
import { UploadType } from '@/gql/graphql';
import { BAlert, BBadge, BButton, BModal } from 'bootstrap-vue-next';
import { formatFileName } from '@/utils/file-utils';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  upload: UploadType;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'success']);

// --- Component state ---
const fileSelected = ref<File | null>(null);
const resumeError = ref<string | null>(null);
const resumeShowSuccess = ref<boolean>(false);
const show = computed<boolean>(() => {
  return props.show;
});

// Create a ref for the hidden file input
const fileInput = ref<HTMLInputElement | null>(null);

// Trigger the file input dialog
function triggerFileSelect() {
  fileInput.value?.click();
}

// When the file input changes, update our fileSelected
function handleFileSelected(e: Event) {
  const target = e.target as HTMLInputElement;

  if (target.files && target.files.length > 1) {
    resumeError.value = 'Please select only one file';
    fileSelected.value = null;
    return;
  } else if (target.files && target.files.length === 1) {
    // check names match
    if (target.files[0].name !== formatFileName(props.upload.sourceFileName)) {
      resumeError.value =
        'File name ' +
        target.files[0].name +
        ' does not match ' +
        formatFileName(props.upload.sourceFileName);
      fileSelected.value = null;
      return;
    } else if (target.files[0].size.toString() !== props.upload.size) {
      resumeError.value =
        'File size does not match: ' +
        target.files[0].size +
        ' is not equal to ' +
        props.upload.size;
      fileSelected.value = null;
      return;
    }

    // file selection successful
    resumeError.value = null;
    fileSelected.value = target.files[0];
  }
}

// --- Handle user actions ---
function onFileSelectionConfirmed() {
  if (fileSelected.value) {
    emits('success', fileSelected.value);
  }
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- Hidden file input element -->
    <input
      type="file"
      ref="fileInput"
      style="display: none"
      @change="handleFileSelected"
    />
    <!-- MODAL: Resume Upload -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="rotate-right" class="me-2" />
        Resume upload
      </template>
      <!-- Content -->
      <div>
        <!-- File needs to be picked from file system -->
        <p>
          Please select the file from your file system to resume the upload of
          <strong>{{ formatFileName(props.upload.sourceFileName) }}</strong
          >?
        </p>
        <!-- Button to trigger file selection -->
        <b-button variant="outline-primary" @click="triggerFileSelect">
          Select File
        </b-button>
        <!-- Display the name of the selected file -->
        <div v-if="fileSelected" class="mt-2">
          <BBadge variant="success">
            Selected file: {{ fileSelected.name }}
          </BBadge>
        </div>

        <!-- Error Message -->
        <b-alert v-model="resumeError" variant="danger" class="mt-3">
          {{ resumeError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!fileSelected || resumeError"
          @click="onFileSelectionConfirmed"
        >
          <span v-if="resumeShowSuccess">
            Upload Request Registered
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Resume Upload </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
